import type { Page } from '@/types/database';

export interface PageTreeNode {
  page: Page;
  children: Page[];
}

export interface PageTree {
  navTree: PageTreeNode[];
  notLinked: Page[];
}

// Single source of truth for grouping a flat page list into the "Main
// Navigation" (top-level pages, each with their own show_in_nav children as
// a one-level dropdown) vs "Not Linked" split — shared by the Studio Pages
// panel and the public site's nav so both render the exact same structure.
export function buildPageTree(pages: Page[]): PageTree {
  const byParent = new Map<string, Page[]>();
  for (const page of pages) {
    if (page.parent_page_id) {
      const siblings = byParent.get(page.parent_page_id) ?? [];
      siblings.push(page);
      byParent.set(page.parent_page_id, siblings);
    }
  }

  const navTree: PageTreeNode[] = [];
  const notLinked: Page[] = [];

  for (const page of pages) {
    if (page.parent_page_id) {
      // Children are only reachable through their parent's node above — an
      // orphaned child whose parent isn't itself in nav has no dropdown to
      // live in, so it falls back to "Not Linked" rather than disappearing.
      const parent = pages.find((candidate) => candidate.id === page.parent_page_id);
      if (!parent?.show_in_nav || !page.show_in_nav) {
        notLinked.push(page);
      }
      continue;
    }

    if (page.show_in_nav) {
      navTree.push({ page, children: byParent.get(page.id)?.filter((child) => child.show_in_nav) ?? [] });
    } else {
      notLinked.push(page);
    }
  }

  return { navTree, notLinked };
}

// Single source of truth for "which page renders for this request" —
// shared by the public site (src/hooks/use-public-site.ts) and Studio's
// initial page selection (src/pages/studio/index.tsx), so both agree on
// what a bare site root resolves to. A requested slug always wins when it
// matches; otherwise falls to the site's explicit home page (`is_home`),
// then to the first page by page_order, so a site with no home page set
// yet (or a stale slug in the URL) still resolves to something.
export function resolveCurrentPage(pages: Page[], requestedSlug: string | undefined): Page | null {
  const home = pages.find((page) => page.is_home) ?? pages[0] ?? null;
  if (!requestedSlug) return home;
  return pages.find((page) => page.slug === requestedSlug) ?? home;
}
